import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, MapPin, User, CheckCircle, Bell, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NotificationModal({ notification, onAcknowledge }) {
  const [isAcknowledging, setIsAcknowledging] = useState(false);

  if (!notification) return null;

  const isCreated = notification.type === 'appointment_created';
  const isCompleted = notification.type === 'appointment_completed';
  const apt = notification.appointment;

  // Determine styling based on notification type
  const getStyle = () => {
    if (isCompleted) return { bg: 'bg-emerald-100', text: 'text-emerald-600', textDark: 'text-emerald-700', btn: 'bg-emerald-600 hover:bg-emerald-700' };
    if (isCreated) return { bg: 'bg-green-100', text: 'text-green-600', textDark: 'text-green-700', btn: 'bg-green-600 hover:bg-green-700' };
    return { bg: 'bg-blue-100', text: 'text-blue-600', textDark: 'text-blue-700', btn: 'bg-blue-600 hover:bg-blue-700' };
  };
  const style = getStyle();

  const getTitle = () => {
    if (isCompleted) return 'Appointment Completed';
    if (isCreated) return 'New Appointment Created';
    return 'Appointment Updated';
  };

  const handleAcknowledge = async () => {
    setIsAcknowledging(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/notifications/acknowledge`, {
        notification_type: notification.type,
        appointment_id: apt.id,
        appointment_title: apt.title
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onAcknowledge();
    } catch (error) {
      console.error('Error acknowledging notification:', error);
      // Still close the modal even if tracking fails
      onAcknowledge();
    } finally {
      setIsAcknowledging(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-[450px] max-h-[85vh]" 
        data-testid="notification-modal"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 sm:gap-3 text-lg sm:text-xl">
            <div className={`p-1.5 sm:p-2 rounded-full ${style.bg}`}>
              {isCompleted ? (
                <CheckCircle2 className={`w-5 h-5 sm:w-6 sm:h-6 ${style.text}`} />
              ) : (
                <Bell className={`w-5 h-5 sm:w-6 sm:h-6 ${style.text}`} />
              )}
            </div>
            <span className={style.textDark}>
              {getTitle()}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          {/* Appointment Title */}
          <h3 className="text-xl font-bold text-slate-800 mb-4">
            {apt.title}
          </h3>

          {/* Details */}
          <div className="space-y-3">
            {/* Team */}
            <div className="flex items-center gap-3 text-slate-600">
              <div 
                className="w-4 h-4 rounded-full" 
                style={{ backgroundColor: apt.team_color || '#64748b' }}
              ></div>
              <span className="font-medium">{apt.team_name}</span>
            </div>

            {/* Date */}
            <div className="flex items-center gap-3 text-slate-600">
              <Calendar className="w-5 h-5 text-slate-400" />
              <span>{apt.date}</span>
            </div>

            {/* Time */}
            <div className="flex items-center gap-3 text-slate-600">
              <Clock className="w-5 h-5 text-slate-400" />
              <span>{apt.start_time}</span>
            </div>

            {/* Location if exists */}
            {apt.location && (
              <div className="flex items-center gap-3 text-slate-600">
                <MapPin className="w-5 h-5 text-slate-400" />
                <span>{apt.location}</span>
              </div>
            )}

            {/* Created/Updated/Completed by */}
            <div className="flex items-center gap-3 text-slate-500 pt-2 border-t border-slate-200">
              <User className="w-5 h-5 text-slate-400" />
              <span className="text-sm">
                {isCompleted ? 'Completed by ' : isCreated ? 'Created by ' : 'Updated by '}
                <strong>{isCompleted ? apt.completed_by_name : isCreated ? apt.created_by_name : apt.updated_by_name}</strong>
              </span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button 
            onClick={handleAcknowledge}
            disabled={isAcknowledging}
            className={`w-full ${style.btn}`}
            data-testid="acknowledge-notification-btn"
          >
            <CheckCircle className="w-5 h-5 mr-2" />
            {isAcknowledging ? 'Confirming...' : 'I Acknowledge'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
